import { Extension } from '@tiptap/core'
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import type { Transaction } from '@tiptap/pm/state'

export interface SearchHighlightOptions {
  searchTerm: string
  highlightClass: string
  currentClass: string
  caseSensitive: boolean
}

export interface SearchHighlightStorage {
  searchTerm: string
  currentIndex: number
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    searchHighlight: {
      setSearchTerm: (searchTerm: string, currentIndex?: number) => ReturnType
      clearSearch: () => ReturnType
      setCurrentSearchIndex: (currentIndex: number) => ReturnType
    }
  }
}

const pluginKey = new PluginKey('searchHighlight')

function createDecorations(
  doc: ProseMirrorNode,
  searchTerm: string,
  currentIndex: number,
  options: SearchHighlightOptions
): DecorationSet {
  const decorations: Decoration[] = []
  
  if (!searchTerm) {
    return DecorationSet.empty
  }
  
  const searchRegex = new RegExp(
    searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
    options.caseSensitive ? 'g' : 'gi'
  )
  
  let matchIndex = 0
  
  doc.descendants((node: ProseMirrorNode, pos: number) => {
    if (node.isText && node.text) {
      const text = node.text
      let match
      
      while ((match = searchRegex.exec(text)) !== null) {
        const from = pos + match.index
        const to = from + match[0].length
        const isCurrent = matchIndex === currentIndex
        
        decorations.push(
          Decoration.inline(from, to, {
            class: isCurrent ? options.currentClass : options.highlightClass,
          })
        )
        
        matchIndex++
      }
    }
  })
  
  return DecorationSet.create(doc, decorations)
}

export const SearchHighlight = Extension.create<SearchHighlightOptions, SearchHighlightStorage>({
  name: 'searchHighlight',

  addOptions() {
    return {
      searchTerm: '',
      highlightClass: 'search-highlight',
      currentClass: 'search-current',
      caseSensitive: false,
    }
  },

  addStorage() {
    return {
      searchTerm: '',
      currentIndex: -1,
    }
  },

  addProseMirrorPlugins() {
    const extension = this

    return [
      new Plugin({
        key: pluginKey,
        // High priority to handle clicks before other plugins (like InlineViewHighlight)
        priority: 1000,
        state: {
          init: () => DecorationSet.empty,
          apply(tr: Transaction, oldState: DecorationSet) {
            const meta = tr.getMeta(pluginKey)

            if (meta?.searchTerm !== undefined) {
              // Store search term in extension storage
              extension.storage.searchTerm = meta.searchTerm
              extension.storage.currentIndex = meta.currentIndex ?? -1

              // Search term changed, recalculate decorations
              return createDecorations(
                tr.doc,
                meta.searchTerm,
                meta.currentIndex ?? -1,
                extension.options
              )
            }

            if (meta?.currentIndex !== undefined) {
              // Update current index in storage
              extension.storage.currentIndex = meta.currentIndex

              // Only current index changed
              return createDecorations(
                tr.doc,
                extension.storage.searchTerm,
                meta.currentIndex,
                extension.options
              )
            }

            // Document changed, recalculate decorations if we have a search term
            if (tr.docChanged && extension.storage.searchTerm) {
              return createDecorations(
                tr.doc,
                extension.storage.searchTerm,
                extension.storage.currentIndex,
                extension.options
              )
            }

            return oldState
          },
        },
        props: {
          decorations(state) {
            return this.getState(state)
          },
          // DOM-level event handler - runs before handleClick
          handleDOMEvents: {
            click: (view, event) => {
              const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })
              if (!pos) return false

              const decorations = pluginKey.getState(view.state) as DecorationSet

              // Find decorations around the click position
              for (let checkPos = Math.max(0, pos.pos - 5); checkPos <= pos.pos + 5; checkPos++) {
                const decos = decorations.find(checkPos, checkPos)
                for (const deco of decos) {
                  const className = (deco as any).type?.attrs?.class
                  if (className === extension.options.highlightClass ||
                      className === extension.options.currentClass) {
                    console.log('[SearchHighlight] DOM click handler - preventing default')
                    event.preventDefault()
                    event.stopPropagation()
                    event.stopImmediatePropagation()

                    // Set cursor to clicked position
                    const $pos = view.state.doc.resolve(pos.pos)
                    const selection = TextSelection.near($pos)
                    const tr = view.state.tr.setSelection(selection)
                    view.dispatch(tr)

                    return true // Event handled
                  }
                }
              }

              return false
            }
          },
          handleClick(view, pos, event) {
            // Check if there are any search decorations at all
            const decorations = this.getState(view.state) as DecorationSet

            // Find decorations at the clicked position and nearby
            // Check a range around the click position since inline decorations might be tricky
            let foundDeco: Decoration | null = null
            let decoPos = -1

            // Search in a small range around the click position
            for (let checkPos = Math.max(0, pos - 5); checkPos <= pos + 5; checkPos++) {
              const decos = decorations.find(checkPos, checkPos)

              for (const deco of decos) {
                const className = (deco as any).type?.attrs?.class
                if (className === extension.options.highlightClass ||
                    className === extension.options.currentClass) {
                  foundDeco = deco
                  decoPos = checkPos
                  console.log('[SearchHighlight] Found decoration at pos:', checkPos, 'class:', className)
                  break
                }
              }

              if (foundDeco) break
            }

            if (foundDeco) {
              // Prevent default behavior
              event.preventDefault()
              event.stopPropagation()

              console.log('[SearchHighlight] Preventing cursor jump, setting to pos:', pos)

              // Create a text selection at the clicked position
              const $pos = view.state.doc.resolve(pos)
              const selection = TextSelection.near($pos)

              const tr = view.state.tr.setSelection(selection)
              view.dispatch(tr)

              return true // Event handled
            }

            return false // Let other handlers process
          },
        },
      }),
    ]
  },

  addCommands() {
    return {
      setSearchTerm: (searchTerm: string, currentIndex = -1) => ({ tr, dispatch }) => {
        if (dispatch) {
          tr.setMeta(pluginKey, { searchTerm, currentIndex })
        }
        return true
      },
      
      clearSearch: () => ({ tr, dispatch }) => {
        if (dispatch) {
          tr.setMeta(pluginKey, { searchTerm: '', currentIndex: -1 })
        }
        return true
      },
      
      setCurrentSearchIndex: (currentIndex: number) => ({ tr, dispatch }) => {
        if (dispatch) {
          tr.setMeta(pluginKey, { currentIndex })
        }
        return true
      },
    }
  },
})